/**
 * Account Manager Command Center (Task 206) — pure presentation logic,
 * extracted from `AccountManagerCommandCenter.tsx` so it is unit-testable
 * without importing React. No fetching, no permission gating — display
 * mapping only. The FE gate (account_manager + multi-client) lives in
 * `AppLanding`; RLS stays the load-bearing security gate.
 *
 * Scope note: the Account Manager surface is driven by `me.client_assignments`
 * (ASSIGNED clients only), NOT the org-wide client list. The follow-up builder
 * below therefore operates on `ClientAssignmentSummary`, not `ClientRow`.
 *
 * The small status→label / status→risk / Arabic-numeral display helpers are
 * the SAME primitives the Owner/Marketing-Manager command centers use, so this
 * module re-exports them from `ownerCommandCenter.logic` rather than
 * duplicating them (clean DRY reuse of pure functions — no refactor of the
 * owner surface). Only the account-manager follow-up builder is AM-specific.
 */

import type { ClientAssignmentSummary } from '../api/types';

export {
  SOON,
  clientStatusLabelAr,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  avatarColor,
  initial,
  toArabicDigits,
  AVATAR_COLORS,
} from './ownerCommandCenter.logic';

/** Icons used by derived follow-ups (a subset of the AM icon set). */
export type FollowUpIcon = 'phone' | 'user-plus';

export interface FollowUp {
  readonly key: string;
  readonly tone: string;
  readonly sev: 'urgent' | 'high' | 'mid';
  readonly sevLabel: string;
  readonly icon: FollowUpIcon;
  readonly title: string;
  readonly why: string;
  readonly to: string;
  readonly action: string;
}

/**
 * Build the "follow-ups needed today" list from REAL assigned-client status
 * only — paused accounts (urgent: re-engage) and onboarding accounts (mid:
 * finish onboarding / first touchpoint). No fabricated signals (no invented
 * ages/deadlines); every item links to the real client overview. Capped at 6.
 * Operates on the account manager's OWN assignments (assigned-only scope).
 */
export function buildFollowUps(
  assignments: ReadonlyArray<ClientAssignmentSummary>,
): ReadonlyArray<FollowUp> {
  const out: FollowUp[] = [];
  for (const a of assignments) {
    if (a.client_status === 'paused') {
      out.push({
        key: `paused-${a.client_id}`,
        tone: 'occ-dec-rose',
        sev: 'urgent',
        sevLabel: 'عاجل',
        icon: 'phone',
        title: `عميل متوقّف يحتاج متابعة عاجلة — ${a.client_name}`,
        why: 'الحساب متوقّف حالياً؛ تواصلي مع العميل وحدّدي خطة إعادة التفعيل قبل أن يتعثّر التسليم.',
        to: `/app/clients/${a.client_id}/overview`,
        action: 'فتح المساحة',
      });
    }
  }
  for (const a of assignments) {
    if (a.client_status === 'onboarding') {
      out.push({
        key: `onb-${a.client_id}`,
        tone: 'occ-dec-plum',
        sev: 'mid',
        sevLabel: 'متوسط',
        icon: 'user-plus',
        title: `عميل جديد قيد التأهيل — ${a.client_name}`,
        why: 'أكملي خطوات التأهيل وثبّتي أول نقطة تواصل لترسيخ بداية العلاقة مع العميل.',
        to: `/app/clients/${a.client_id}/overview`,
        action: 'متابعة التأهيل',
      });
    }
  }
  return out.slice(0, 6);
}
